import { useCallback, useEffect, useState } from 'react'
import { SNAPSHOT_TRIGGER_LABELS, erfuelltRecht, type ControllingCell, type ControllingMatrix, type ControllingRow, geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { fetchControlling, fetchSnapshots, takeSnapshot, type ControllingSnapshot } from '../api/project-controlling.api'
import { fetchPositions } from '../api/projects-phase-d.api'
import { alsTag, alsZeitpunkt } from '../project-labels'
import { ActualEntriesList } from './ActualEntriesList'

const schmal = inlineInput.replace('w-full', '')
const heute = () => new Date().toISOString().slice(0, 10)
/** Stundenzahl ohne Einheit — die Einheit steht im Spaltenkopf. */
const zahl = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
/** Stunden mit dem Kürzel aus den Artikel-Einheiten (Fließtext der Hinweise). */
const std = (n: number | null | undefined, einheit: string) => (n == null ? '—' : `${zahl(n)} ${einheit}`)
/** Betrag im Geldformat (`geld.ts`); leer → „—". */
const geld = (n: number | null | undefined) => geldText(n) || '—'

const hinweisText = (daten: string, einheit: string): Record<string, (h: { count?: number; amount?: number | null }) => string> => ({
  ACTUALS_NO_COST_RATE: (h) => `${std(h.count, einheit)} ohne Kostensatz — zählen mit 0`,
  ACTUALS_NO_PLANNED_RATE: (h) => `${h.count} Arbeitsschritte ohne Sollsatz — ${daten} unvollständig`,
  ACTUALS_UNASSIGNED: (h) => `${std(h.count, einheit)} ohne Arbeitsschritt — Zeile „nicht zugeordnet"`,
  CONSUMABLE_NO_PURCHASE_PRICE: (h) => `${h.count} Verbrauchszeilen ohne Einkaufspreis — zählen mit 0`,
  INCOMING_IN_APPROVAL: (h) => `Einkauf in Freigabe ${geld(h.amount)} — zählt noch nicht`,
})

const QUELLE: Record<string, string> = {
  'projects.controlling.calculationNotBooked': 'Kalkulation nicht gebucht',
  'projects.controlling.calculationNoLines': 'keine Verkaufszeile aus einer Kalkulation',
  'projects.controlling.timeTrackingNotBooked': 'Zeiterfassung mit Projektbezug nicht gebucht',
  'projects.controlling.procurementNotBooked': 'Stückliste nicht gebucht — Einkauf ohne Sollwert',
  'projects.controlling.incomingInvoicesNotBooked': 'Eingangsrechnungen nicht gebucht — verbuchter Einkauf nur aus Verbrauch',
  'projects.controlling.incomingInvoicesNotConnected': 'Eingangsrechnungen noch nicht angebunden',
  'projects.controlling.billingNotBooked': 'Verkauf & Abrechnung nicht gebucht — Verkauf ohne Sollwert',
  'projects.controlling.noPriceRight': 'Beträge nur mit Preisrecht',
}

/**
 * AP-35 Detailblatt 8 3. — **Reiter Nachkalkulation.**
 *
 * Live zum Stichtag, je Projekt oder je Position; Maßstab ist die Spalte
 * Projektdaten (`terms.data`) (rot: Kosten darüber, Verkauf darunter). Ein festgehaltener
 * Stand (bei Fertigstellung, Archivierung oder von Hand) lässt sich daneben
 * aufrufen. Darunter die Buchungen.
 *
 * Mit `festePosition` steht der Reiter an der Position (Auftraggeber 19.9.2026):
 * fest auf diese Position und nur live — festgehaltene Stände gelten dem ganzen
 * Projekt und zeigten hier fremde Zahlen.
 */
export function ProjectControllingTab({ projectId, festePosition }: { projectId: string; festePosition?: string }) {
  const terms = useTerminology()
  const { user } = useAuthStore()
  const preise = erfuelltRecht('projects.price.view', user?.permissions)
  const [bis, setBis] = useState(heute())
  const [positionId, setPositionId] = useState(festePosition ?? '')
  const [stand, setStand] = useState<string>('live')
  const [m, setM] = useState<ControllingMatrix | null>(null)
  const [staende, setStaende] = useState<ControllingSnapshot[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [positions, setPositions] = useState<{ id: string; number: number; name: string }[]>([])

  useEffect(() => {
    if (festePosition) return
    fetchPositions(projectId)
      .then((r) => setPositions(r.positions))
      .catch(() => setPositions([]))
  }, [projectId, festePosition])

  const laden = useCallback(async () => {
    try {
      const [matrix, snaps] = await Promise.all([fetchControlling(projectId, bis, positionId || undefined), fetchSnapshots(projectId)])
      setM(matrix)
      setStaende(snaps)
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Nachkalkulation konnte nicht geladen werden.'))
    }
  }, [projectId, bis, positionId])

  useEffect(() => {
    void laden()
  }, [laden])

  const gezeigt = stand === 'live' ? m : (staende.find((s) => s.id === stand)?.matrix ?? m)
  const festgehalten = staende.find((s) => s.id === stand)
  // Die Schreibweise gilt heute — auch für einen älteren Stand, der sie noch nicht kennt.
  const einheit = m?.hourUnit ?? gezeigt?.hourUnit ?? 'h'
  // Die Spalte „aus Kalkulation" nur, wenn sie etwas trägt — sonst erklärt sie der Hinweis.
  const mitKalkulation = !!gezeigt?.sources.calculation.available

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="text-muted-foreground">
          Stichtag
          <input type="date" disabled={stand !== 'live'} className={`${schmal} ml-2 w-40`} value={bis} onChange={(e) => setBis(e.target.value || heute())} />
        </label>
        {!festePosition && (
          <select className={`${schmal} w-64`} disabled={stand !== 'live'} value={positionId} onChange={(e) => setPositionId(e.target.value)} aria-label={terms.position}>
            <option value="">alle {terms.positionPlural}</option>
            {positions.map((p) => (
              <option key={p.id} value={p.id}>
                {terms.position} {p.number} · {p.name}
              </option>
            ))}
          </select>
        )}
        {!festePosition && (
          <>
            <select className={`${schmal} w-72`} value={stand} onChange={(e) => setStand(e.target.value)} aria-label="Stand">
              <option value="live">heute (live)</option>
              {staende.map((s) => (
                <option key={s.id} value={s.id}>
                  Stand {SNAPSHOT_TRIGGER_LABELS[s.trigger]} {alsTag(s.takenAt)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1.5 hover:bg-muted"
              onClick={() =>
                void takeSnapshot(projectId)
                  .then((r) => {
                    setHinweis(r.replaced ? 'Stand von heute ersetzt.' : 'Stand festgehalten.')
                    return laden()
                  })
                  .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
              }
            >
              Stand festhalten
            </button>
          </>
        )}
        {gezeigt && <span className="ml-auto text-muted-foreground">Leistungsstand {gezeigt.progressPercent} %</span>}
      </div>
      {festgehalten && <p className="text-xs text-muted-foreground">Festgehalten am {alsZeitpunkt(festgehalten.takenAt)} — spätere Korrekturen ändern diese Zahlen nicht.</p>}
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}

      {gezeigt && (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full whitespace-nowrap text-sm">
            <thead className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th rowSpan={2} className="px-3 py-1.5 text-left align-bottom" />
                <th colSpan={mitKalkulation ? 4 : 3} className="border-l border-border px-3 pt-1.5 text-center font-semibold text-foreground">
                  Stunden ({einheit})
                </th>
                {preise && (
                  <th colSpan={mitKalkulation ? 4 : 3} className="border-l border-border px-3 pt-1.5 text-center font-semibold text-foreground">
                    Betrag
                  </th>
                )}
                <th rowSpan={2} className="border-l border-border px-3 py-1.5 text-right align-bottom" title="Ist ÷ Soll">
                  Anteil
                </th>
              </tr>
              <tr>
                <Kopf mitKalkulation={mitKalkulation} daten={terms.data} />
                {preise && <Kopf mitKalkulation={mitKalkulation} daten={terms.data} />}
              </tr>
            </thead>
            <tbody>
              {gezeigt.rows.map((r) => (
                <Zeile key={r.key} r={r} preise={preise} mitKalkulation={mitKalkulation} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {gezeigt && (
        <div className={`${formCardSm} space-y-1 text-xs text-muted-foreground`}>
          <h3 className={cardTitle}>Hinweise</h3>
          {gezeigt.hints.length === 0 && <p>Keine.</p>}
          {gezeigt.hints.map((h) => (
            <p key={h.code}>{hinweisText(terms.data, einheit)[h.code]?.(h) ?? h.code}</p>
          ))}
          {Object.values(gezeigt.sources)
            .filter((q) => !q.available && q.reason)
            .map((q) => (
              <p key={q.reason}>{QUELLE[q.reason!] ?? q.reason}</p>
            ))}
        </div>
      )}

      <div>
        <h3 className={`${cardTitle} mb-2`}>Buchungen</h3>
        <ActualEntriesList projectId={projectId} positionId={positionId || null} />
      </div>
    </div>
  )
}

const zelle = 'px-3 py-1.5 text-right tabular-nums'

/** Unterkopf einer Gruppe (Stunden oder Betrag): Soll, Ist, Abweichung. */
function Kopf({ mitKalkulation, daten }: { mitKalkulation: boolean; daten: string }) {
  return (
    <>
      {mitKalkulation && <th className="border-l border-border px-3 pb-1.5 text-right font-normal">Kalkulation</th>}
      <th className={`${mitKalkulation ? '' : 'border-l border-border'} px-3 pb-1.5 text-right font-normal`} title={daten}>
        Soll
      </th>
      <th className="px-3 pb-1.5 text-right font-normal" title="verbucht">
        Ist
      </th>
      <th className="px-3 pb-1.5 text-right font-normal" title="Ist − Soll">
        Abweichung
      </th>
    </>
  )
}

/** Eine Zahl; die Abweichung mit Vorzeichen. Leer, wo die Zeile den Wert nicht führt (Stunden beim Einkauf), „—", wo er fehlt. */
function Zahl({ n, format, abweichung }: { n: number | null | undefined; format: (n: number | null | undefined) => string; abweichung?: boolean }) {
  if (n === undefined) return null
  if (n === null) return <span className="text-muted-foreground/60">—</span>
  return <>{abweichung && n > 0 ? `+${format(n)}` : format(n)}</>
}

function Zeile({ r, preise, mitKalkulation }: { r: ControllingRow; preise: boolean; mitKalkulation: boolean }) {
  const summe = r.kind === 'SUM' || r.kind === 'MARGIN'
  const leise = r.kind === 'FORECAST' || r.kind === 'SUPPLEMENT'
  // Rot wird die Abweichung, an der das Backend misst: mit Preisrecht der Betrag, sonst die Stunden.
  const rot = 'font-medium text-destructive'
  const gruppe = (feld: keyof ControllingCell, format: (n: number | null | undefined) => string, alarm: boolean) => (
    <>
      {mitKalkulation && (
        <td className={`${zelle} border-l border-border text-muted-foreground`}>
          <Zahl n={r.calculation?.[feld]} format={format} />
        </td>
      )}
      <td className={`${zelle} ${mitKalkulation ? '' : 'border-l border-border'}`}>
        <Zahl n={r.planned?.[feld]} format={format} />
      </td>
      <td className={zelle}>
        <Zahl n={r.actual?.[feld]} format={format} />
      </td>
      <td className={`${zelle} ${alarm && r.alert ? rot : ''}`}>
        <Zahl n={r.difference?.[feld]} format={format} abweichung />
      </td>
    </>
  )
  return (
    <tr
      className={`border-b border-border last:border-0 ${summe ? 'border-t-2 bg-muted/30 font-medium' : ''} ${
        // Die beiden Zeilen, auf die man schaut (Auftraggeber 18.9.2026): etwas größer.
        r.key === 'cost' || r.kind === 'MARGIN' ? 'text-base font-semibold' : ''
      } ${leise ? 'text-muted-foreground' : ''}`}
    >
      <td className={`px-3 py-1.5 ${r.kind === 'PHASE' || r.kind === 'UNASSIGNED' ? 'pl-6' : ''}`}>{r.label}</td>
      {gruppe('hours', zahl, !preise)}
      {preise && gruppe('amount', geld, true)}
      <td className={`${zelle} border-l border-border ${r.alert ? rot : ''}`}>{r.percent == null ? '' : `${r.percent} %`}</td>
    </tr>
  )
}
